/*
 * Shipping Destination Popup
 * Submits Shopify's native localization form so the real market context changes
 * (currency, pricing, availability, shipping, checkout).
 */
(function () {
  'use strict';

  var COOKIE_NAME = 'kindaura_shipping_destination';

  function getCookie(name) {
    var match = document.cookie.match(new RegExp('(^| )' + name + '=([^;]+)'));
    return match ? decodeURIComponent(match[2]) : null;
  }

  function setCookie(name, value, days) {
    var maxAge = Math.max(1, parseInt(days, 10) || 365) * 24 * 60 * 60;
    var expires = new Date(Date.now() + maxAge * 1000).toUTCString();
    var secure = location.protocol === 'https:' ? '; Secure' : '';
    var base =
      name +
      '=' +
      encodeURIComponent(value) +
      '; max-age=' +
      maxAge +
      '; expires=' +
      expires +
      '; path=/; SameSite=Lax' +
      secure;

    // Host-only first: this always works, whatever the domain looks like.
    document.cookie = base;

    // Then a domain-scoped twin so the choice survives apex <-> www. If the
    // browser rejects it (public suffix, IP host) the host-only one still stands.
    var host = location.hostname.replace(/^www\./, '');
    if (host.indexOf('.') !== -1) {
      try {
        document.cookie = base + '; domain=.' + host;
      } catch (error) {
        /* host-only cookie is enough */
      }
    }

    // Belt and braces: some browsers drop cookies in private/ITP contexts.
    try {
      localStorage.setItem(name, value);
    } catch (error) {
      /* storage unavailable */
    }
  }

  function hasStoredChoice(name) {
    if (getCookie(name)) return true;
    try {
      return !!localStorage.getItem(name);
    } catch (error) {
      return false;
    }
  }

  function clearStoredChoice(name) {
    var host = location.hostname.replace(/^www\./, '');
    var expired = '=; expires=Thu, 01 Jan 1970 00:00:00 GMT; path=/';
    document.cookie = name + expired;
    if (host.indexOf('.') !== -1) document.cookie = name + expired + '; domain=.' + host;
    try {
      localStorage.removeItem(name);
    } catch (error) {
      /* storage unavailable */
    }
  }

  // Combining diacritical marks U+0300-U+036F, built without escape sequences
  // so this file survives any transport that mangles backslashes.
  var COMBINING_MARKS = new RegExp(
    '[' + String.fromCharCode(768) + '-' + String.fromCharCode(879) + ']',
    'g'
  );

  function normalize(str) {
    if (!str) return '';
    var out = String(str).toLowerCase();
    if (typeof out.normalize === 'function') {
      out = out.normalize('NFD').replace(COMBINING_MARKS, '');
    }
    return out.replace(/[^a-z0-9]+/g, ' ').trim();
  }

  function debounce(fn, wait) {
    var t;
    return function () {
      var args = arguments;
      var ctx = this;
      clearTimeout(t);
      t = setTimeout(function () {
        fn.apply(ctx, args);
      }, wait);
    };
  }

  var FOCUSABLE =
    'button:not([disabled]), input:not([disabled]), [href], select, textarea, [tabindex]:not([tabindex="-1"])';

  class ShippingDestinationPopup extends HTMLElement {
    constructor() {
      super();
      this.isOpen = false;
      this.selected = null;
      this.timer = null;
      this.scrollY = 0;
      this.onKeydown = this.onKeydown.bind(this);
    }

    connectedCallback() {
      this.form = this.querySelector('form');
      this.dialog = this.querySelector('.sdp__dialog');
      this.countryInput = this.querySelector('[data-sdp-country-input]');
      this.list = this.querySelector('[data-sdp-list]');
      this.search = this.querySelector('[data-sdp-search]');
      this.searchReset = this.querySelector('[data-sdp-search-reset]');
      this.footer = this.querySelector('[data-sdp-footer]');
      this.selectedLabel = this.querySelector('[data-sdp-selected-label]');
      this.emptyMessage = this.querySelector('[data-sdp-empty]');
      this.divider = this.querySelector('[data-sdp-divider]');
      this.overlay = this.querySelector('[data-sdp-overlay]');
      this.closeButton = this.querySelector('[data-sdp-close]');

      if (!this.form || !this.list) return;

      this.options = Array.prototype.slice.call(this.querySelectorAll('[data-sdp-option]'));
      this.options.forEach(function (option) {
        option.dataset.sdpSearchKey =
          normalize(option.dataset.countryName) + ' ' + normalize(option.dataset.countryCode);
      });

      this.dismissible = this.dataset.dismissible === 'true';
      this.testMode = this.dataset.testMode === 'true';
      this.expiry = parseInt(this.dataset.expiry, 10) || 365;
      this.delay = (parseInt(this.dataset.delay, 10) || 0) * 1000;
      this.currentCountry = this.dataset.currentCountry || '';
      this.currentCountryName = this.dataset.currentCountryName || '';
      this.showTrigger = this.dataset.showTrigger === 'true';
      this.manualOpen = false;

      this.bindEvents();
      this.replaceStockCountrySelectors();
      this.injectTrigger();
      this.maybeOpen();
    }

    disconnectedCallback() {
      clearTimeout(this.timer);
      clearTimeout(this.hideTimer);
      document.removeEventListener('keydown', this.onKeydown);
    }

    bindEvents() {
      var self = this;

      this.list.addEventListener('click', function (event) {
        var option = event.target.closest('[data-sdp-option]');
        if (option) self.selectCountry(option);
      });

      if (this.search) {
        this.search.addEventListener(
          'input',
          debounce(function () {
            self.filter(self.search.value);
          }, 120)
        );
        this.search.addEventListener('keydown', function (event) {
          if (event.key === 'Enter') {
            event.preventDefault();
            var first = self.options.find(function (option) {
              return !option.parentElement.hidden;
            });
            if (first) self.selectCountry(first);
          }
        });
      }

      if (this.searchReset) {
        this.searchReset.addEventListener('click', function () {
          self.search.value = '';
          self.filter('');
          self.search.focus();
        });
      }

      this.form.addEventListener('submit', function () {
        // Persist before the page reloads into the new market.
        setCookie(COOKIE_NAME, self.selected || self.currentCountry, self.expiry);
      });

      if (this.closeButton) {
        this.closeButton.addEventListener('click', function () {
          self.dismiss();
        });
      }

      if (this.overlay) {
        this.overlay.addEventListener('click', function () {
          if (self.dismissible || self.manualOpen) self.dismiss();
        });
      }

      // Theme editor support
      document.addEventListener('shopify:section:select', function (event) {
        if (self.closest('.shopify-section') === event.target) self.open();
      });
      document.addEventListener('shopify:section:deselect', function (event) {
        if (self.closest('.shopify-section') === event.target) self.close();
      });
    }

    /*
     * The theme ships Shopify's stock country picker in several places (header,
     * mobile menu drawer, footer, announcement bar). Those are driven straight
     * off localization.available_countries, so they list every country enabled
     * in Markets - including ones we have no shipping zone for. Swap each one
     * for a trigger that opens this popup, so the filtered list is the only way
     * a destination can ever be chosen. Language pickers are left alone.
     */
    replaceStockCountrySelectors() {
      var self = this;
      try {
        var forms = document.querySelectorAll('form.localization-form');

        Array.prototype.forEach.call(forms, function (form) {
          if (self.contains(form)) return; // never touch our own form
          if (!form.querySelector('input[name="country_code"]')) return; // language picker
          if (form.querySelector('input[name="locale_code"]')) return; // combined picker, leave it

          var host = form.closest('localization-form') || form;
          if (!host.parentNode) return;

          if (self.showTrigger) {
            host.parentNode.replaceChild(self.buildTrigger('sdp-trigger--inline'), host);
          } else {
            host.parentNode.removeChild(host);
          }
        });
      } catch (error) {
        // A theme update could change this markup. Never take the page down for it.
        if (window.console && console.warn) {
          console.warn('[shipping-destination-popup] could not replace stock selector:', error);
        }
      }
    }

    buildTrigger(extraClass) {
      var code = (this.currentCountry || '').toLowerCase();
      var name = this.currentCountryName || this.currentCountry;

      var button = document.createElement('button');
      button.type = 'button';
      button.className = 'sdp-trigger' + (extraClass ? ' ' + extraClass : '');
      button.setAttribute('aria-haspopup', 'dialog');
      button.setAttribute('aria-label', 'Shipping to ' + name + '. Change shipping destination');

      var flag = document.createElement('span');
      flag.className = 'sdp-trigger__flag';
      if (code) {
        var img = document.createElement('img');
        img.src = 'https://flagcdn.com/' + code + '.svg';
        img.alt = '';
        img.width = 22;
        img.height = 16;
        img.decoding = 'async';
        img.addEventListener('error', function () {
          img.remove();
        });
        flag.appendChild(img);
      }

      var label = document.createElement('span');
      label.className = 'sdp-trigger__name';
      label.textContent = name;

      button.appendChild(flag);
      button.appendChild(label);

      var self = this;
      button.addEventListener('click', function (event) {
        event.preventDefault();
        self.open(true);
      });

      return button;
    }

    injectTrigger() {
      if (!this.showTrigger) return;

      var hosts = ['.header__icons', 'header .header__icon--cart'];
      var host = null;
      for (var i = 0; i < hosts.length; i++) {
        host = document.querySelector(hosts[i]);
        if (host) break;
      }

      var button = this.buildTrigger();

      if (host && host.classList.contains('header__icons')) {
        host.insertBefore(button, host.firstChild);
      } else if (host && host.parentNode) {
        host.parentNode.insertBefore(button, host);
      } else {
        button.classList.add('sdp-trigger--floating');
        document.body.appendChild(button);
      }

      this.trigger = button;
    }

    maybeOpen() {
      // Test mode doubles as the reset switch: it wipes any saved choice so the
      // merchant gets a true first-visit experience on every load.
      if (this.testMode) clearStoredChoice(COOKIE_NAME);

      // Test mode is the ONLY thing that overrides a saved choice. The theme
      // editor used to override it too, which made the popup reappear on every
      // page load in Customize and looked exactly like broken persistence.
      // In the editor you now open it by selecting the section instead.
      if (!this.testMode && hasStoredChoice(COOKIE_NAME)) return;

      var self = this;
      this.timer = setTimeout(function () {
        // Re-check: another tab, or the localization reload, may have saved a
        // choice while this delay was running.
        if (!self.testMode && hasStoredChoice(COOKIE_NAME)) return;
        self.open();
      }, this.delay);
    }

    open(manual) {
      if (this.isOpen) return;
      // Cancel a close still waiting to set hidden=true, or it fires mid-open
      // and blanks a popup the visitor just reopened.
      clearTimeout(this.hideTimer);
      this.manualOpen = manual === true;
      if (this.closeButton) {
        // A visitor who opened this themselves must always be able to back out.
        this.closeButton.hidden = !(this.dismissible || this.manualOpen);
      }
      this.isOpen = true;
      this.hidden = false;
      this.lastFocused = document.activeElement;

      this.scrollY = window.scrollY;
      document.body.classList.add('sdp-open');

      var self = this;
      requestAnimationFrame(function () {
        self.classList.add('sdp--visible');
        // Always open at the top of the list: the priority destinations are
        // ordered deliberately and must be the first thing the visitor sees.
        if (self.list) self.list.scrollTop = 0;
        if (self.search) self.search.focus({ preventScroll: true });
      });

      document.addEventListener('keydown', this.onKeydown);
    }

    close() {
      if (!this.isOpen) return;
      this.isOpen = false;
      this.manualOpen = false;
      this.classList.remove('sdp--visible');
      document.body.classList.remove('sdp-open');
      document.removeEventListener('keydown', this.onKeydown);

      var self = this;
      clearTimeout(this.hideTimer);
      this.hideTimer = setTimeout(function () {
        self.hidden = true;
      }, 250);

      if (this.lastFocused && typeof this.lastFocused.focus === 'function') {
        this.lastFocused.focus();
      }
    }

    dismiss() {
      // Keep whatever market Shopify already resolved; just stop asking.
      setCookie(COOKIE_NAME, this.currentCountry || 'dismissed', this.expiry);
      this.close();
    }

    onKeydown(event) {
      if (event.key === 'Escape' && (this.dismissible || this.manualOpen)) {
        event.preventDefault();
        this.dismiss();
        return;
      }
      if (event.key !== 'Tab' || !this.dialog) return;

      var focusable = Array.prototype.filter.call(
        this.dialog.querySelectorAll(FOCUSABLE),
        function (el) {
          return el.offsetParent !== null;
        }
      );
      if (!focusable.length) return;

      var first = focusable[0];
      var last = focusable[focusable.length - 1];

      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    }

    filter(term) {
      var query = normalize(term);
      var visible = 0;

      this.options.forEach(function (option) {
        var match = !query || option.dataset.sdpSearchKey.indexOf(query) !== -1;
        option.parentElement.hidden = !match;
        if (match) visible++;
      });

      if (this.divider) this.divider.hidden = query.length > 0;
      if (this.emptyMessage) this.emptyMessage.hidden = visible > 0;
      if (this.searchReset) this.searchReset.hidden = !query.length;
      if (this.list) this.list.scrollTop = 0;
    }

    selectCountry(option) {
      var code = option.dataset.countryCode;
      var name = option.dataset.countryName;

      this.options.forEach(function (el) {
        el.classList.remove('sdp__option--selected');
        el.setAttribute('aria-selected', 'false');
      });
      option.classList.add('sdp__option--selected');
      option.setAttribute('aria-selected', 'true');

      this.selected = code;
      if (this.countryInput) this.countryInput.value = code;

      // Save immediately. The submit handler saves again, but if the visitor
      // navigates away before submitting we still stop re-prompting them.
      setCookie(COOKIE_NAME, code, this.expiry);

      if (this.selectedLabel) {
        this.selectedLabel.textContent = 'Shipping to ' + name;
      }
      if (this.footer) {
        this.footer.hidden = false;
        var self = this;
        requestAnimationFrame(function () {
          self.footer.classList.add('sdp__footer--visible');
        });
      }
    }
  }

  if (!customElements.get('shipping-destination-popup')) {
    customElements.define('shipping-destination-popup', ShippingDestinationPopup);
  }
})();
